import { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, User, Clock, Share2, ThumbsUp, Loader2, Check } from 'lucide-react';
import { type Article, toggleArticleLike, getSupabaseImageUrl } from '../lib/supabase';
import { Helmet } from 'react-helmet-async';
import { Breadcrumbs } from './Breadcrumbs';
import { ArticleTags } from './ArticleTags';
import { useImageContext } from '../contexts/ImageContext';

interface ArticleViewProps {
  article: Article;
   onBack: () => void;
  onTagClick?: (tag: string) => void;
  onCategoryClick?: (slug: string) => void;
  parentCategory?: { name: string; slug: string };
}

const ImagePlaceholder = () => (
  <div className="w-full h-[450px] bg-gray-100 animate-pulse rounded-lg" />
);

const LazyImage = ({ src, alt }: { src: string; alt: string }) => {
  const { processUrl } = useImageContext();
  const processedSrc = processUrl(src);

  return (
    <img
      src={processedSrc}
      alt={alt}
      className="w-full h-[450px] object-cover rounded-lg bg-gray-50"
      itemProp="image"
      loading="eager"
    />
  );
};

export default function ArticleView({
  article,
  onBack,
  onTagClick,
  onCategoryClick,
  parentCategory
}: ArticleViewProps) {
  const [isLiking, setIsLiking] = useState(false);
  const [hasLiked, setHasLiked] = useState(article.user_has_liked || false);
  const [likesCount, setLikesCount] = useState(article.likes_count);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [readingTime, setReadingTime] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (article?.featured_image) {
      const processedUrl = getSupabaseImageUrl(article.featured_image);
      setImageUrl(processedUrl);
    }
  }, [article?.featured_image]);

  useEffect(() => {
    if (article?.content) {
      const words = article.content.trim().split(/\s+/).length;
      const minutes = Math.ceil(words / 200);
      setReadingTime(`${minutes} min de leitura`);
    }
  }, [article?.content]);

  const handleLike = async () => {
    try {
      setIsLiking(true);
      const liked = await toggleArticleLike(article.id);
      setHasLiked(liked);
      setLikesCount((prevCount: number) => liked ? prevCount + 1 : prevCount - 1);
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share && !window.location.href.includes('localhost')) {
        await navigator.share({
          title: article.title,
          text: article.excerpt || '',
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <article className="max-w-4xl mx-auto px-4 py-8" itemScope itemType="http://schema.org/BlogPosting">
      <Helmet>
        <title>{`${article.title} - Blog Roda de Proa`}</title>
        <meta name="description" content={article.excerpt || ''} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || ''} />
        <meta property="og:image" content={imageUrl || ''} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={article.published_at || ''} />
        <meta property="article:modified_time" content={article.updated_at || ''} />
        <meta property="article:author" content={article.author?.name || ''} />
        <meta property="article:section" content={article.category?.name || ''} />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': window.location.href
            },
            'headline': article.title,
            'description': article.excerpt,
            'image': imageUrl,
            'author': {
              '@type': 'Person',
              'name': article.author?.name
            },
            'publisher': {
              '@type': 'Organization',
              'name': 'Blog Roda de Proa',
              'logo': {
                '@type': 'ImageObject',
                'url': 'https://blog.rodadeproa.com.br/logo.png'
              }
            },
            'datePublished': article.published_at,
            'dateModified': article.updated_at,
            'articleSection': article.category?.name,
            'articleBody': article.content?.replace(/<[^>]*>/g, ''),
            'keywords': `navegação, ${article.category?.name?.toLowerCase()}, náutica, barcos`
          })}
        </script>
      </Helmet>

      <div className="mb-8">
        <button 
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Voltar para Artigos
        </button>
      </div>

      <Breadcrumbs
  parentCategory={parentCategory}
  category={article.category}
  article={article}
  onCategoryClick={onCategoryClick}
/>
  
      <header className="mb-8">
        <div className="text-blue-600 text-sm font-semibold mb-2" itemProp="articleSection">
          {article.category?.name}
        </div>
        <h1 itemProp="headline" className="text-4xl font-bold text-gray-900 mb-4">
          {article.title}
        </h1>

        {/* Article Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mb-6">
            <ArticleTags
              tags={article.tags}
              onTagClick={onTagClick}
              size="medium"
              interactive={true}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center text-gray-600 gap-4">
          <div className="flex items-center" itemProp="author" itemScope itemType="http://schema.org/Person">
            <User className="w-4 h-4 mr-2" />
            <span itemProp="name">{article.author?.name}</span>
          </div>
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            <time itemProp="datePublished" dateTime={article.published_at || ''}>
              {new Date(article.published_at || '').toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            <span>{readingTime}</span>
          </div>
        </div>
      </header>

      {article.featured_image && (
        <figure className="mb-8 relative pt-[56.25%] bg-gray-50 rounded-lg overflow-hidden">
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={imageUrl || article.featured_image}
            alt={article.title}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            itemProp="image"
            loading="eager"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              console.error('Error loading image:', e);
              const img = e.target as HTMLImageElement;
              img.onerror = null;
              img.src = '/placeholder-image.svg';
              setImageLoaded(true);
            }}
          />
        </figure>
      )}

      <div 
        itemProp="articleBody"
        className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-blue-600 hover:prose-a:text-blue-700 prose-strong:font-bold prose-strong:text-gray-900 prose-em:text-gray-800 prose-code:text-gray-800 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:not-italic prose-li:text-gray-600"
      >
        {article.content && (
          <div dangerouslySetInnerHTML={{ __html: article.content }} />
        )}
      </div>

      <footer className="mt-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              hasLiked 
                ? 'bg-blue-100 text-blue-600' 
                : 'text-gray-600 hover:text-blue-600'
            }`}
            onClick={handleLike}
            disabled={isLiking}
            aria-label={`Curtir artigo (${likesCount} curtidas)`}
          >
            {isLiking ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <ThumbsUp className={`w-5 h-5 ${hasLiked ? 'fill-current' : ''}`} />
            )}
            <span>{likesCount} Curtidas</span>
          </button>
          <div className="relative">
            <button 
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 hover:text-blue-600"
              onClick={handleShare}
              aria-label="Compartilhar artigo"
            >
              <Share2 className="w-5 h-5" />
              <span>Compartilhar</span>
            </button>
            {showCopiedMessage && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg flex items-center">
                <Check className="w-4 h-4 mr-1" />
                Link copiado!
              </div>
            )}
          </div>
        </div>
      </footer>
    </article>
  );
}